import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useAuth, API } from "@/App";
import { 
  ArrowLeft, User, Calendar, Activity, Apple, Droplet,
  Target, TrendingUp, TrendingDown, AlertTriangle, Clock,
  MessageSquare, FileText, Send, Plus, CheckCircle, Download, Loader2,
  Camera, Star
} from "lucide-react";
import { toast } from "sonner";

const PatientProfile = () => {
  const { patientId } = useParams();
  const { token } = useAuth();
  const location = useLocation();
  const isPartnerView = location.pathname.startsWith("/partner");
  const [loading, setLoading] = useState(true);
  const [patientData, setPatientData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Note form
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteData, setNoteData] = useState({ observation: "", recommendation: "", follow_up_date: "" });
  const [submittingNote, setSubmittingNote] = useState(false);
  
  // Message form
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [submittingMessage, setSubmittingMessage] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Meal journal
  const [mealPhotos, setMealPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [feedbackData, setFeedbackData] = useState({});
  const [submittingFeedback, setSubmittingFeedback] = useState(null);

  useEffect(() => {
    fetchPatientData();
  }, [patientId, token]);

  useEffect(() => {
    if (activeTab === "meal journal") {
      fetchMealPhotos();
    }
  }, [activeTab]);

  const fetchMealPhotos = async () => {
    setLoadingPhotos(true);
    try {
      const res = await fetch(`${API}/meal-photos/patient/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMealPhotos(data.photos || []);
      }
    } catch (err) {
      console.error("Error fetching meal photos:", err);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const submitFeedback = async (photoId) => {
    const fb = feedbackData[photoId];
    if (!fb?.rating) {
      toast.error("Please select a star rating");
      return;
    }
    setSubmittingFeedback(photoId);
    try {
      const res = await fetch(`${API}/meal-photos/${photoId}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ rating: fb.rating, comment: fb.comment || "" })
      });
      if (res.ok) {
        toast.success("Feedback submitted");
        fetchMealPhotos();
        setFeedbackData((prev) => {
          const next = { ...prev };
          delete next[photoId];
          return next;
        });
      } else {
        toast.error("Failed to submit feedback");
      }
    } catch (err) {
      toast.error("Error submitting feedback");
    } finally {
      setSubmittingFeedback(null);
    }
  };

  const fetchPatientData = async () => {
    setLoading(true);
    try {
      const apiPath = isPartnerView 
        ? `${API}/partner-admin/patient/${patientId}`
        : `${API}/dietitian/patient/${patientId}`;
      const response = await fetch(apiPath, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setPatientData(data);
      } else {
        toast.error("Failed to load patient data");
      }
    } catch (error) {
      console.error("Fetch patient error:", error);
      toast.error("Failed to load patient data");
    } finally {
      setLoading(false);
    }
  };

  const submitNote = async () => {
    if (!noteData.observation.trim()) {
      toast.error("Please enter an observation");
      return;
    }
    
    setSubmittingNote(true);
    try {
      const response = await fetch(`${API}/dietitian/notes`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          patient_id: patientId,
          ...noteData,
        }),
      });
      
      if (response.ok) {
        toast.success("Note added successfully");
        setNoteData({ observation: "", recommendation: "", follow_up_date: "" });
        setShowNoteForm(false);
        fetchPatientData();
      } else {
        toast.error("Failed to add note");
      }
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSubmittingNote(false);
    }
  };

  const sendMessage = async () => {
    if (!messageText.trim()) {
      toast.error("Please enter a message");
      return;
    }
    
    setSubmittingMessage(true);
    try {
      const response = await fetch(`${API}/messages/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          recipient_id: patientId,
          content: messageText,
        }),
      });
      
      if (response.ok) {
        toast.success("Message sent successfully");
        setMessageText("");
        setShowMessageForm(false);
        fetchPatientData();
      } else {
        toast.error("Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSubmittingMessage(false);
    }
  };

  const downloadPatientPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await fetch(`${API}/dietitian/patient/${patientId}/report`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${patient?.name || "patient"}_report_${new Date().toISOString().split("T")[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Report downloaded successfully");
      } else {
        toast.error("Failed to generate report");
      }
    } catch (error) {
      console.error("Download PDF error:", error);
      toast.error("Failed to download report");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const getComplianceBadge = (score) => {
    if (score >= 71) return { label: "Good", color: "bg-success/10 text-success", icon: TrendingUp };
    if (score >= 41) return { label: "Moderate", color: "bg-warning/10 text-warning", icon: Activity };
    return { label: "Poor", color: "bg-destructive/10 text-destructive", icon: TrendingDown };
  };

  const getGlycemicBadge = (score) => {
    if (score >= 71) return { label: "Good Control", color: "text-success" };
    if (score >= 41) return { label: "Fair Control", color: "text-warning" };
    return { label: "Poor Control", color: "text-destructive" };
  };

  const getHba1cStatus = (hba1c) => {
    if (!hba1c) return { label: "N/A", color: "text-muted-foreground" };
    if (hba1c < 5.7) return { label: "Normal", color: "text-success" };
    if (hba1c < 6.5) return { label: "Prediabetes", color: "text-warning" };
    if (hba1c < 7) return { label: "Diabetic - Good", color: "text-blue-600" };
    if (hba1c < 8) return { label: "Diabetic - Fair", color: "text-warning" };
    return { label: "Diabetic - Needs Improvement", color: "text-destructive" };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const backPath = isPartnerView ? "/partner/patients" : "/physician";

  if (!patientData) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Patient not found</p>
        <Link to={backPath}>
          <Button variant="outline" className="mt-4">Back to Patients</Button>
        </Link>
      </div>
    );
  }

  const { patient, partner, meals, glucose_logs, compliance_score, estimated_hba1c, glycemic_control_score, notes, messages } = patientData;
  const complianceBadge = getComplianceBadge(compliance_score || 0);
  const glycemicBadge = getGlycemicBadge(glycemic_control_score || 0);
  const hba1cStatus = getHba1cStatus(estimated_hba1c);
  const ComplianceIcon = complianceBadge.icon;

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Link to={backPath} className="inline-flex items-center gap-2 text-muted-foreground hover:text-secondary">
        <ArrowLeft className="w-4 h-4" />
        Back to Patients
      </Link>

      {/* Patient Header */}
      <div className="flex flex-col md:flex-row gap-6">
        <Card className="flex-1 p-6 rounded-2xl border-orange-100">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
              <User className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-secondary">{patient?.name || "Unknown"}</h1>
              <p className="text-muted-foreground">{patient?.email}</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {patient?.diabetes_type && (
                  <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-medium">
                    {patient.diabetes_type}
                  </span>
                )}
                {partner && (
                  <span className="px-2 py-1 rounded-full bg-green-50 text-green-600 text-xs font-medium">
                    {partner.organization_name}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="rounded-full"
                onClick={() => setShowNoteForm(true)}
              >
                <FileText className="w-4 h-4 mr-1" />
                Add Note
              </Button>
              <Button 
                size="sm" 
                className="rounded-full bg-blue-600 hover:bg-blue-700"
                onClick={() => setShowMessageForm(true)}
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Message
              </Button>
              <Button 
                variant="outline"
                size="sm" 
                className="rounded-full text-green-600 border-green-200 hover:bg-green-50"
                onClick={downloadPatientPdf}
                disabled={downloadingPdf}
                data-testid="download-report-btn"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-1" />
                )}
                {downloadingPdf ? "Generating..." : "Download PDF"}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Clinical Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 rounded-2xl border-orange-100">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${complianceBadge.color.split(' ')[0]} flex items-center justify-center`}>
              <ComplianceIcon className={`w-6 h-6 ${complianceBadge.color.split(' ')[1]}`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary">{compliance_score || 0}%</p>
              <p className="text-xs text-muted-foreground">Compliance Score</p>
              <span className={`text-xs font-medium ${complianceBadge.color.split(' ')[1]}`}>
                {complianceBadge.label}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-2xl border-orange-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <Droplet className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary">
                {estimated_hba1c ? `${estimated_hba1c}%` : "N/A"}
              </p>
              <p className="text-xs text-muted-foreground">Est. HbA1c</p>
              <span className={`text-xs font-medium ${hba1cStatus.color}`}>
                {hba1cStatus.label}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-2xl border-orange-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <Target className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary">{glycemic_control_score || 0}</p>
              <p className="text-xs text-muted-foreground">Glycemic Control</p>
              <span className={`text-xs font-medium ${glycemicBadge.color}`}>
                {glycemicBadge.label}
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-2xl border-orange-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
              <Apple className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-secondary">{meals?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Meals (30 days)</p>
              <span className="text-xs text-muted-foreground">
                {glucose_logs?.length || 0} glucose logs
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-orange-100">
        {["overview", "meals", "glucose", "notes", "meal journal"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors capitalize ${
              activeTab === tab
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-muted-foreground hover:text-secondary"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Recent Meals */}
          <Card className="p-4 rounded-2xl border-orange-100">
            <h3 className="font-bold text-secondary mb-4 flex items-center gap-2">
              <Apple className="w-5 h-5 text-primary" />
              Recent Meals
            </h3>
            <div className="space-y-3">
              {meals?.slice(0, 5).map((meal, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-orange-50/50 rounded-xl">
                  <div>
                    <p className="font-medium text-secondary text-sm">
                      {meal.detected_foods?.map(f => f.name).join(", ") || "Unknown meal"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(meal.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">{Math.round(meal.total_carbs || 0)}g</p>
                    <p className="text-xs text-muted-foreground">carbs</p>
                  </div>
                </div>
              )) || <p className="text-muted-foreground text-sm">No meals recorded</p>}
            </div>
          </Card>

          {/* Recent Messages */}
          <Card className="p-4 rounded-2xl border-orange-100">
            <h3 className="font-bold text-secondary mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Recent Messages
            </h3>
            <div className="space-y-3">
              {messages?.slice(0, 5).map((msg, index) => (
                <div key={index} className="p-3 bg-blue-50/50 rounded-xl">
                  <p className="text-sm text-secondary">{msg.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {msg.sender_name} • {new Date(msg.sent_at).toLocaleDateString()}
                  </p>
                </div>
              )) || <p className="text-muted-foreground text-sm">No messages yet</p>}
            </div>
          </Card>
        </div>
      )}

      {activeTab === "meals" && (
        <Card className="rounded-2xl border-orange-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-orange-50/50">
              <tr>
                <th className="text-left p-4 font-medium text-secondary">Date</th>
                <th className="text-left p-4 font-medium text-secondary">Meal</th>
                <th className="text-left p-4 font-medium text-secondary">Carbs</th>
                <th className="text-left p-4 font-medium text-secondary">GI Impact</th>
              </tr>
            </thead>
            <tbody>
              {meals?.map((meal, index) => (
                <tr key={index} className="border-t border-orange-100">
                  <td className="p-4 text-sm text-muted-foreground">
                    {new Date(meal.timestamp).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <p className="text-sm font-medium text-secondary">
                      {meal.detected_foods?.map(f => f.name).join(", ") || "Unknown"}
                    </p>
                  </td>
                  <td className="p-4">
                    <span className="font-bold text-primary">{Math.round(meal.total_carbs || 0)}g</span>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      meal.glycemic_impact === "Low" ? "bg-success/10 text-success" :
                      meal.glycemic_impact === "Medium" ? "bg-warning/10 text-warning" :
                      "bg-destructive/10 text-destructive"
                    }`}>
                      {meal.glycemic_impact || "N/A"}
                    </span>
                  </td>
                </tr>
              )) || (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-muted-foreground">
                    No meals recorded
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      )}

      {activeTab === "glucose" && (
        <Card className="rounded-2xl border-orange-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-purple-50/50">
              <tr>
                <th className="text-left p-4 font-medium text-secondary">Date</th>
                <th className="text-left p-4 font-medium text-secondary">Reading</th>
                <th className="text-left p-4 font-medium text-secondary">Type</th>
                <th className="text-left p-4 font-medium text-secondary">Notes</th>
              </tr>
            </thead>
            <tbody>
              {glucose_logs?.map((log, index) => (
                <tr key={index} className="border-t border-orange-100">
                  <td className="p-4 text-sm text-muted-foreground">
                    {new Date(log.timestamp).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <span className={`font-bold ${
                      log.glucose_value < 70 ? "text-blue-600" :
                      log.glucose_value <= 180 ? "text-success" :
                      "text-destructive"
                    }`}>
                      {log.glucose_value} mg/dL
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-1 rounded-full bg-purple-50 text-purple-600 text-xs font-medium">
                      {log.reading_type || "Random"}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    {log.notes || "-"}
                  </td>
                </tr>
              )) || (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-muted-foreground">
                    No glucose readings recorded
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      )}

      {activeTab === "notes" && (
        <div className="space-y-4">
          <Button 
            onClick={() => setShowNoteForm(true)}
            className="rounded-full bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Clinical Note
          </Button>
          
          {notes?.length > 0 ? (
            notes.map((note, index) => (
              <Card key={index} className="p-4 rounded-2xl border-orange-100">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-secondary">{note.dietitian_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(note.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {note.follow_up_date && (
                    <span className="px-2 py-1 rounded-full bg-warning/10 text-warning text-xs font-medium">
                      Follow-up: {note.follow_up_date}
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">Observation</p>
                    <p className="text-sm text-secondary">{note.observation}</p>
                  </div>
                  {note.recommendation && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">Recommendation</p>
                      <p className="text-sm text-secondary">{note.recommendation}</p>
                    </div>
                  )}
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-8 rounded-2xl border-orange-100 text-center">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No clinical notes yet</p>
            </Card>
          )}
        </div>
      )}

      {/* Meal Journal Tab */}
      {activeTab === "meal journal" && (
        <div className="space-y-4">
          {loadingPhotos ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse bg-white rounded-2xl h-64 border border-orange-100/30" />
              ))}
            </div>
          ) : mealPhotos.length === 0 ? (
            <Card className="p-8 rounded-2xl border-orange-100 text-center">
              <Camera className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No meal photos uploaded yet</p>
              <p className="text-xs text-muted-foreground mt-1">Patient hasn't shared any meal photos for review</p>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {mealPhotos.map((photo) => (
                <Card key={photo.photo_id} className="rounded-2xl border-orange-100 overflow-hidden" data-testid={`journal-photo-${photo.photo_id}`}>
                  <img
                    src={photo.image_url}
                    alt={photo.caption}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="px-2 py-0.5 rounded-full bg-orange-50 text-primary text-xs font-medium capitalize">
                        {photo.meal_type}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(photo.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-secondary">{photo.caption}</p>

                    {/* Existing feedback */}
                    {photo.feedback ? (
                      <div className="p-3 bg-green-50 rounded-xl border border-green-100">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-green-700">{photo.feedback.physician_name}</span>
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star key={s} className={`w-3.5 h-3.5 ${s <= photo.feedback.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`} />
                            ))}
                          </div>
                        </div>
                        {photo.feedback.comment && (
                          <p className="text-xs text-green-800">{photo.feedback.comment}</p>
                        )}
                      </div>
                    ) : (
                      /* Feedback form */
                      <div className="space-y-2 p-3 bg-blue-50/50 rounded-xl border border-blue-100/50">
                        <p className="text-xs font-medium text-blue-700">Rate this meal</p>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <button
                              key={s}
                              onClick={() => setFeedbackData((prev) => ({
                                ...prev,
                                [photo.photo_id]: { ...prev[photo.photo_id], rating: s }
                              }))}
                              data-testid={`star-${photo.photo_id}-${s}`}
                            >
                              <Star className={`w-6 h-6 cursor-pointer transition-colors ${
                                s <= (feedbackData[photo.photo_id]?.rating || 0)
                                  ? "fill-yellow-400 text-yellow-400"
                                  : "text-gray-300 hover:text-yellow-300"
                              }`} />
                            </button>
                          ))}
                        </div>
                        <Textarea
                          placeholder="Add a comment about this meal..."
                          value={feedbackData[photo.photo_id]?.comment || ""}
                          onChange={(e) => setFeedbackData((prev) => ({
                            ...prev,
                            [photo.photo_id]: { ...prev[photo.photo_id], comment: e.target.value }
                          }))}
                          className="text-sm rounded-lg border-blue-200 min-h-[60px]"
                          data-testid={`comment-${photo.photo_id}`}
                        />
                        <Button
                          onClick={() => submitFeedback(photo.photo_id)}
                          disabled={submittingFeedback === photo.photo_id || !feedbackData[photo.photo_id]?.rating}
                          className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-sm"
                          data-testid={`submit-feedback-${photo.photo_id}`}
                        >
                          {submittingFeedback === photo.photo_id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          Submit Feedback
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Note Modal */}
      {showNoteForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6 rounded-2xl">
            <h3 className="font-bold text-secondary text-lg mb-4">Add Clinical Note</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-secondary">Observation *</label>
                <Textarea
                  placeholder="Enter your clinical observation..."
                  value={noteData.observation}
                  onChange={(e) => setNoteData({ ...noteData, observation: e.target.value })}
                  className="mt-1 rounded-xl"
                  rows={3}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-secondary">Recommendation</label>
                <Textarea
                  placeholder="Enter recommendations for the patient..."
                  value={noteData.recommendation}
                  onChange={(e) => setNoteData({ ...noteData, recommendation: e.target.value })}
                  className="mt-1 rounded-xl"
                  rows={3}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-secondary">Follow-up Date</label>
                <Input
                  type="date"
                  value={noteData.follow_up_date}
                  onChange={(e) => setNoteData({ ...noteData, follow_up_date: e.target.value })}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowNoteForm(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button 
                  onClick={submitNote} 
                  disabled={submittingNote}
                  className="rounded-full bg-blue-600 hover:bg-blue-700"
                >
                  {submittingNote ? "Saving..." : "Save Note"}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Send Message Modal */}
      {showMessageForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md p-6 rounded-2xl">
            <h3 className="font-bold text-secondary text-lg mb-4">Send Message to {patient?.name}</h3>
            <div className="space-y-4">
              <Textarea
                placeholder="Type your message here..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                className="rounded-xl"
                rows={4}
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowMessageForm(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button 
                  onClick={sendMessage} 
                  disabled={submittingMessage}
                  className="rounded-full bg-blue-600 hover:bg-blue-700"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submittingMessage ? "Sending..." : "Send Message"}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PatientProfile;
